import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, Mail, User, Phone, AlertCircle, UserPlus, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_RE = /^[+\d\s().-]{8,20}$/;

function formatError(detail) {
  if (!detail) return "Une erreur s'est produite";
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const Register = () => {
  const { register } = useAuth();
  const location = useLocation();
  const redirectTo = location.state?.from || '/mon-compte';

  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState(false);
  const [resending, setResending] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!EMAIL_RE.test(form.email)) {
      setError('Format email invalide');
      return;
    }
    if (!PHONE_RE.test(form.phone)) {
      setError('Numéro de téléphone invalide');
      return;
    }
    if (form.password.length < 6) {
      setError('Mot de passe : 6 caractères minimum');
      return;
    }
    if (form.password !== form.confirm) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setSubmitting(true);
    try {
      await register(form.email, form.password, form.name, form.phone);
      setPending(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: form.email });
    } finally {
      setResending(false);
    }
  };

  if (pending) {
    return (
      <div className="min-h-[80vh] bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8 border border-gray-100 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-3" data-testid="register-pending-title">
            Compte créé !
          </h1>
          <p className="text-gray-600 text-sm mb-2">
            Un email de confirmation a été envoyé à :
          </p>
          <p className="font-semibold text-gray-900 mb-5 break-all">{form.email}</p>
          <p className="text-gray-600 text-sm mb-6">
            Cliquez sur le lien dans l'email pour activer votre compte. Pensez à vérifier votre dossier
            <strong> spam / courrier indésirable</strong>.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded p-4 text-xs text-gray-600 mb-6">
            Vous n'avez rien reçu après quelques minutes ?
            <button
              onClick={resend}
              disabled={resending}
              data-testid="register-resend-link"
              className="block mt-2 text-[#E42313] font-semibold hover:underline disabled:opacity-50"
            >
              {resending ? 'Envoi…' : "Renvoyer l'email de confirmation"}
            </button>
          </div>
          <Link
            to="/connexion"
            state={{ from: redirectTo }}
            className="inline-block text-sm text-gray-600 hover:text-[#E42313]"
          >
            Déjà confirmé ? Se connecter
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-100">
          <div className="text-center mb-6">
            <div className="inline-flex w-12 h-12 bg-[#E42313]/10 rounded-full items-center justify-center mb-3">
              <UserPlus className="w-6 h-6 text-[#E42313]" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Créer un compte</h1>
            <p className="text-gray-500 text-sm mt-1">Pour passer commande et suivre vos achats</p>
          </div>

          {error && (
            <div data-testid="register-error" className="mb-5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nom complet *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-name"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded focus:outline-none focus:border-[#E42313]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded focus:outline-none focus:border-[#E42313]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Téléphone *</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-phone"
                  required
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  placeholder="06 12 34 56 78 ou +33 6 12…"
                  className="w-full pl-10 pr-4 py-3 border rounded focus:outline-none focus:border-[#E42313]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Mot de passe *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-password"
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => set('password', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded focus:outline-none focus:border-[#E42313]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Confirmer le mot de passe *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="register-confirm"
                  type="password"
                  required
                  value={form.confirm}
                  onChange={(e) => set('confirm', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded focus:outline-none focus:border-[#E42313]"
                />
              </div>
            </div>
            <button
              data-testid="register-submit"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#E42313] text-white py-3 rounded font-semibold hover:bg-[#C11A0F] transition-colors disabled:opacity-50"
            >
              {submitting ? 'Création...' : 'Créer mon compte'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-6">
            Déjà un compte ?{' '}
            <Link
              to="/connexion"
              state={{ from: redirectTo }}
              className="text-[#E42313] font-semibold hover:underline"
            >
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
